import {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import * as React from "react";
import {useState} from "react";
import {LoadingButton} from "@mui/lab";
import {useMutation} from "react-query";
import {useSnackbar} from "notistack";
import {AudienceService} from "crqlar-react-common/dist/services/audience/audience.service";
import {MixpanelService} from "crqlar-react-common/dist/services/analytics/mixpanel.service";
import Button from "@mui/material/Button";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import CloseIcon from "@mui/icons-material/Close";
import AudienceForm from "./AudienceForm";

interface Props {
    audience?: Audience
    open: boolean
    onClose: () => void
    /**
     * The audience as it came back from the server — created or updated, so a caller that opened
     * the dialog to get an audience (the automation editor's Audience step) can select the result
     * without a round trip. Callers that only refresh a list ignore the argument, and the delete
     * path passes none: there is nothing left to select.
     */
    onSaved?: (audience?: Audience) => void
}

/**
 * The marketing module's audience editor. The form itself — name, channels, size cards, rules —
 * lives in {@link AudienceForm}, which the automation editor's Audience step embeds as well, so
 * the two can never drift. This dialog is the chrome around it: the title, the delete path, and
 * the create/update mutation.
 */
const AudienceDialog = (props: Props) => {

    const {t} = useTranslation()
    // Read-only users may open the dialog to view an audience, but cannot create or edit anything.
    const readOnly = useReadOnly()
    const {enqueueSnackbar} = useSnackbar()
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [tmpAudience, setTmpAudience] = useState<Audience>({...props.audience})

    const saveAudienceMut = useMutation(
        'saveAudienceMut',
        async () => {
            let creatingNewAudience = !tmpAudience?.id
            let response = creatingNewAudience ? await AudienceService.createAudience(tmpAudience) : await AudienceService.updateAudience(tmpAudience)
            creatingNewAudience && MixpanelService.getInstance().track('audience created', {
                "name": tmpAudience.name,
                "id": response.id
            })
            // The saved audience, not the draft: a caller that has to select the result needs the
            // id the server minted. A create response with no body still hands back what was sent.
            props.onSaved?.({...tmpAudience, ...response})
        }
    )


    const deleteAudienceMut = useMutation(
        ['deleteAudience', tmpAudience.id],
        () => AudienceService.deleteAudience({id: [tmpAudience.id!]}),
        {
            onSuccess: () => {
                enqueueSnackbar(t('Audience deleted successfully'), {variant: 'success'})
                props.onSaved?.()
                props.onClose()
            },
            onError: () => {
                enqueueSnackbar(t('Error deleting audience'), {variant: 'error'})
            },
        }
    )
    const busy = saveAudienceMut.isLoading || deleteAudienceMut.isLoading

    // One render for both modes: creating starts from templates or a prompt, updating starts from the
    // rules the audience already has and reshapes them by hand or with the AI change panel.
    const creating = !props.audience?.id

    const audienceContent = () => {
        return (
            <>
                <DialogTitle sx={{pb: 1}}>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                        <Typography variant={'h6'} fontWeight={600} noWrap>
                            {creating ? t('Create an audience') : t('Update an audience')}
                        </Typography>
                        <Stack>
                            <IconButton onClick={props.onClose} size={'small'} aria-label={t('Close')}>
                                <CloseIcon/>
                            </IconButton>
                        </Stack>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    <AudienceForm
                        audience={tmpAudience}
                        onChange={setTmpAudience}
                        savedAudience={props.audience}
                        readOnly={readOnly}
                    />
                </DialogContent>

                <DialogActions sx={{justifyContent: 'space-between', paddingX: 3, paddingY: 4}}>
                    {/* The destructive action sits alone on the far side, away from Cancel and Update. */}
                    <Stack direction={'row'}>
                        {!creating && (
                            <Button color={'error'} disabled={readOnly || busy} onClick={() => setConfirmDelete(true)}>
                                {t('Delete')}
                            </Button>
                        )}
                    </Stack>
                    <Stack direction={'row'} gap={1}>
                        <Button onClick={props.onClose} disabled={busy}>
                            {t('Cancel')}
                        </Button>
                        <LoadingButton
                            variant={'contained'}
                            onClick={() => {
                                saveAudienceMut.mutate()
                            }}
                            loading={saveAudienceMut.isLoading}
                            disabled={readOnly || busy || !tmpAudience?.name || !tmpAudience?.filter}
                        >
                            {creating ? t('Create audience') : t('Update audience')}
                        </LoadingButton>
                    </Stack>
                </DialogActions>

            </>
        )
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="lg"
        >


            {audienceContent()}

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth={'xs'}>
                <DialogTitle>{t('Delete audience')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Delete "{{name}}"? Connected channels stop receiving it. This cannot be undone.', {name: tmpAudience.name ?? ''})}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)}>{t('Cancel')}</Button>
                    <LoadingButton
                        variant={'contained'}
                        color={'error'}
                        loading={deleteAudienceMut.isLoading}
                        onClick={() => {
                            setConfirmDelete(false)
                            deleteAudienceMut.mutate()
                        }}
                    >
                        {t('Delete')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Dialog>
    )

}

export default AudienceDialog;
